import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { menuCategoriesAPI, menuItemsAPI, restaurantInfoAPI } from '../../services/api';

function AdminDashboard() {
  const [stats, setStats] = useState({
    totalCategories: 0,
    totalItems: 0,
    activeItems: 0,
    inactiveItems: 0,
    recentItems: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [categoriesResponse, itemsResponse] = await Promise.all([
        menuCategoriesAPI.getAll(),
        menuItemsAPI.getAll()
      ]);

      const categories = categoriesResponse.data.data;
      const items = itemsResponse.data.data;
      
      const activeItems = items.filter(item => item.is_available);
      const inactiveItems = items.filter(item => !item.is_available);
      const recentItems = items
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setStats({
        totalCategories: categories.length,
        totalItems: items.length,
        activeItems: activeItems.length,
        inactiveItems: inactiveItems.length,
        recentItems
      });
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Add Menu Item',
      description: 'Create a new menu item',
      icon: 'fas fa-plus-circle',
      color: 'success',
      link: '/admin/menu-items/new'
    },
    {
      title: 'Manage Categories',
      description: 'Organize menu categories',
      icon: 'fas fa-list',
      color: 'primary',
      link: '/admin/categories'
    },
    {
      title: 'Restaurant Info',
      description: 'Update contact details',
      icon: 'fas fa-info-circle',
      color: 'info',
      link: '/admin/restaurant-info'
    },
    {
      title: 'View Website',
      description: 'See customer view',
      icon: 'fas fa-external-link-alt',
      color: 'secondary',
      link: '/',
      external: true
    }
  ];

  if (loading) {
    return (
      <div className="dashboard-loading text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
        <button className="btn btn-outline-danger ms-3" onClick={fetchDashboardData}>
          <i className="fas fa-redo me-2"></i>Retry
        </button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Welcome Section */}
      <div className="welcome-section mb-4">
        <div className="row align-items-center">
          <div className="col-md-8">
            <h1 className="welcome-title">Welcome to Cioconat CMS</h1>
            <p className="welcome-subtitle">
              Manage your restaurant's menu and information from this central dashboard.
            </p>
          </div>
          <div className="col-md-4 text-end">
            <div className="last-updated">
              <small className="text-muted">
                <i className="fas fa-clock me-2"></i>
                Last updated: {new Date().toLocaleString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="stat-card bg-primary">
            <div className="stat-icon">
              <i className="fas fa-list"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.totalCategories}</h3>
              <p className="stat-label">Categories</p>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="stat-card bg-success">
            <div className="stat-icon">
              <i className="fas fa-utensils"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.totalItems}</h3>
              <p className="stat-label">Total Items</p>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="stat-card bg-info">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.activeItems}</h3>
              <p className="stat-label">Active Items</p>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="stat-card bg-warning">
            <div className="stat-icon">
              <i className="fas fa-pause-circle"></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.inactiveItems}</h3>
              <p className="stat-label">Inactive Items</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="section-title">Quick Actions</h4>
          <div className="row">
            {quickActions.map((action, index) => (
              <div key={index} className="col-md-3 mb-3">
                {action.external ? (
                  <a href={action.link} className="quick-action-card" target="_blank" rel="noopener noreferrer">
                    <div className={`action-icon bg-${action.color}`}>
                      <i className={action.icon}></i>
                    </div>
                    <h6 className="action-title">{action.title}</h6>
                    <p className="action-description">{action.description}</p>
                  </a>
                ) : (
                  <Link to={action.link} className="quick-action-card">
                    <div className={`action-icon bg-${action.color}`}>
                      <i className={action.icon}></i>
                    </div>
                    <h6 className="action-title">{action.title}</h6>
                    <p className="action-description">{action.description}</p>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Items */}
      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-clock me-2"></i>
                Recent Menu Items
              </h5>
              <Link to="/admin/menu-items" className="btn btn-outline-primary btn-sm">
                View All
              </Link>
            </div>
            <div className="card-body">
              {stats.recentItems.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Status</th>
                        <th>Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.name}</strong>
                            {item.description && (
                              <>
                                <br />
                                <small className="text-muted">
                                  {item.description.substring(0, 50)}...
                                </small>
                              </>
                            )}
                          </td>
                          <td>{item.category_name}</td>
                          <td>
                            <span className="badge bg-success">JD{item.price}</span>
                          </td>
                          <td>
                            <span className={`badge ${item.is_available ? 'bg-success' : 'bg-secondary'}`}>
                              {item.is_available ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <small>{new Date(item.created_at).toLocaleDateString()}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-utensils fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No menu items found</p>
                  <Link to="/admin/menu-items/new" className="btn btn-primary">
                    Add Your First Item
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                Menu Overview
              </h5>
            </div>
            <div className="card-body">
              <div className="overview-item">
                <div className="overview-label">Active Items</div>
                <div className="overview-value text-success">{stats.activeItems}</div>
                <div className="overview-bar">
                  <div 
                    className="overview-progress bg-success" 
                    style={{ width: `${(stats.activeItems / stats.totalItems) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div className="overview-item">
                <div className="overview-label">Inactive Items</div>
                <div className="overview-value text-warning">{stats.inactiveItems}</div>
                <div className="overview-bar">
                  <div 
                    className="overview-progress bg-warning" 
                    style={{ width: `${(stats.inactiveItems / stats.totalItems) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div className="overview-item">
                <div className="overview-label">Categories</div>
                <div className="overview-value text-primary">{stats.totalCategories}</div>
                <div className="overview-bar">
                  <div className="overview-progress bg-primary" style={{ width: '100%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .welcome-section {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem;
          border-radius: 15px;
          margin-bottom: 2rem;
        }

        .welcome-title {
          font-size: 2.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .welcome-subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
          margin-bottom: 0;
        }

        .stat-card {
          background: white;
          border-radius: 15px;
          padding: 1.5rem;
          display: flex;
          align-items: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease;
          color: white;
        }

        .stat-card:hover {
          transform: translateY(-5px);
        }

        .stat-icon {
          font-size: 2.5rem;
          margin-right: 1rem;
          opacity: 0.8;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.9;
          margin-bottom: 0;
        }

        .bg-primary { background-color: #3498db !important; }
        .bg-success { background-color: #2ecc71 !important; }
        .bg-info { background-color: #17a2b8 !important; }
        .bg-warning { background-color: #f39c12 !important; }

        .section-title {
          color: #2c3e50;
          font-weight: 600;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e9ecef;
        }

        .quick-action-card {
          background: white;
          border: none;
          border-radius: 15px;
          padding: 1.5rem;
          text-align: center;
          text-decoration: none;
          color: #2c3e50;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          display: block;
          height: 100%;
        }

        .quick-action-card:hover {
          transform: translateY(-5px);
          color: #2c3e50;
          text-decoration: none;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        .action-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.5rem;
          color: white;
        }

        .action-title {
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .action-description {
          font-size: 0.9rem;
          color: #6c757d;
          margin-bottom: 0;
        }

        .card {
          border: none;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          border-radius: 15px;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          border-radius: 15px 15px 0 0 !important;
          padding: 1.25rem 1.5rem;
        }

        .overview-item {
          margin-bottom: 1.5rem;
        }

        .overview-item:last-child {
          margin-bottom: 0;
        }

        .overview-label {
          font-size: 0.9rem;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }

        .overview-value {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .overview-bar {
          height: 8px;
          background-color: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .overview-progress {
          height: 100%;
          transition: width 0.3s ease;
        }

        .table th {
          border-top: none;
          font-weight: 600;
          color: #2c3e50;
        }

        .dashboard-loading {
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;